import { ReportData, AdSpendSource, SalesSource, TradeSales, JobData, ReportPeriod } from '../types/reportTypes';
import { codySalesData } from './codySalesData'; // April Cody data
import { codyMayData } from './codyMayData'; // May Cody data  
import { codySalesJuneData } from './codySalesJuneData'; // June Cody data

// Helper function to get month name
const getMonthName = (month: number): string => {
  return new Date(2000, month - 1).toLocaleString('default', { month: 'long' });
};

// Helper function to merge and de-duplicate sources by name
const mergeSourcesByName = <T extends { source: string }>(
  arrays: T[][],
  propertyExtractor: (item: T) => { [key: string]: number }
): T[] => {
  const sourceMap = new Map<string, { source: string } & { [key: string]: number }>();

  arrays.forEach(array => {
    array.forEach(item => {
      const sourceName = item.source;
      const extracted = propertyExtractor(item);
      
      if (!sourceMap.has(sourceName)) {
        const initialObject = { source: sourceName } as { source: string } & { [key: string]: number };
        
        // Add properties with initial value of 0
        Object.keys(extracted).forEach(key => {
          initialObject[key] = 0;
        });
        
        sourceMap.set(sourceName, initialObject);
      }
      
      const existing = sourceMap.get(sourceName)!;
      
      // Add values from current item to existing totals
      Object.entries(extracted).forEach(([key, value]) => {
        existing[key] = (existing[key] || 0) + value;
      });
    });
  });

  return Array.from(sourceMap.values()) as unknown as T[];
};

// Aggregate KPI data from all three months
const aggregateKPIs = () => {
  return {
    adSpend: codySalesData.kpis.adSpend + codyMayData.kpis.adSpend + codySalesJuneData.kpis.adSpend,
    totalSales: codySalesData.kpis.totalSales + codyMayData.kpis.totalSales + codySalesJuneData.kpis.totalSales,
    grossProfit: codySalesData.kpis.grossProfit + codyMayData.kpis.grossProfit + codySalesJuneData.kpis.grossProfit,
    salesCommissions: codySalesData.kpis.salesCommissions + codyMayData.kpis.salesCommissions + codySalesJuneData.kpis.salesCommissions,
    managerFees: codySalesData.kpis.managerFees + codyMayData.kpis.managerFees + codySalesJuneData.kpis.managerFees,
    companyOH: codySalesData.kpis.companyOH + codyMayData.kpis.companyOH + codySalesJuneData.kpis.companyOH,
    netProfit: codySalesData.kpis.netProfit + codyMayData.kpis.netProfit + codySalesJuneData.kpis.netProfit,
    // Calculate net profit percentage over the entire quarter
    netProfitPercent: (codySalesData.kpis.netProfit + codyMayData.kpis.netProfit + codySalesJuneData.kpis.netProfit) / 
                      (codySalesData.kpis.totalSales + codyMayData.kpis.totalSales + codySalesJuneData.kpis.totalSales),
    // Calculate gross profit percentage over the entire quarter
    grossProfitPercent: (codySalesData.kpis.grossProfit + codyMayData.kpis.grossProfit + codySalesJuneData.kpis.grossProfit) / 
                        (codySalesData.kpis.totalSales + codyMayData.kpis.totalSales + codySalesJuneData.kpis.totalSales)
  };
};

// Combine ad spend data from all three months (Cody reports typically have no ad spend)
const aggregateAdSpendBySource = (): AdSpendSource[] => {
  return mergeSourcesByName<AdSpendSource>(
    [codySalesData.adSpendBySource, codyMayData.adSpendBySource, codySalesJuneData.adSpendBySource],
    (item) => ({ spend: item.spend })
  ).sort((a, b) => b.spend - a.spend);
};

// Combine sales data by source from all three months
const aggregateSalesBySource = (): SalesSource[] => {
  return mergeSourcesByName<SalesSource>(
    [codySalesData.salesBySource, codyMayData.salesBySource, codySalesJuneData.salesBySource],
    (item) => ({ totalSales: item.totalSales, count: item.count, grossProfit: item.grossProfit })
  ).sort((a, b) => b.totalSales - a.totalSales);
};

// Combine sales data by trade from all three months
const aggregateSalesByTrade = (): TradeSales[] => {
  // Create a map to store aggregated trade data
  const tradeMap = new Map<string, TradeSales>();
  
  // Process each month's data
  [codySalesData.salesByTrade, codyMayData.salesByTrade, codySalesJuneData.salesByTrade].forEach(monthData => {
    monthData.forEach(tradeItem => {
      const tradeName = tradeItem.trade;
      
      if (!tradeMap.has(tradeName)) {
        tradeMap.set(tradeName, {
          trade: tradeName,
          totalSales: 0,
          count: 0,
          grossProfit: 0
        });
      }
      
      const existing = tradeMap.get(tradeName)!;
      existing.totalSales += tradeItem.totalSales;
      existing.count += tradeItem.count;
      existing.grossProfit += tradeItem.grossProfit;
    });
  });
  
  // Sort by total sales descending
  return Array.from(tradeMap.values()).sort((a, b) => b.totalSales - a.totalSales);
};

// Combine job data from all three months
const aggregateJobData = (): JobData[] => {
  return [...codySalesData.jobData, ...codyMayData.jobData, ...codySalesJuneData.jobData];
};

// Export the CC Q2 2025 quarterly report data
export const ccQ2Data: ReportData = {
  kpis: aggregateKPIs(),
  adSpendBySource: aggregateAdSpendBySource(),
  salesBySource: aggregateSalesBySource(),
  salesByTrade: aggregateSalesByTrade(),
  jobData: aggregateJobData(),
  period: {
    month: 4, // First month of Q2 (April)
    year: 2025,
    quarter: 2, // Q2
    startMonth: 4, // April
    endMonth: 6   // June
  }
};

// Additional data for trend analysis in the quarterly report
export interface CCQ2TrendData {
  monthlyTrends: {
    month: string;
    totalSales: number;
    grossProfit: number;
    netProfit: number;
    adSpend: number;
    roi: number;
    grossMarginPct: number;
    netMarginPct: number;
    salesCount: number;
    avgSaleValue: number;
    expectedCommission: number;
  }[];
  sourcePerformance: SourcePerformance[];
  leadSourceTrends: {
    source: string;
    april: { sales: number; count: number; roi: number };
    may: { sales: number; count: number; roi: number };
    june: { sales: number; count: number; roi: number };
    q2Total: { sales: number; count: number; roi: number };
  }[];
}

// Extended interface for source performance
interface SourcePerformance extends SalesSource {
  adSpend: number;
  roi: number;
  costPerSale: number;
}

// Generate trend data for CC Q2 analysis
export const ccQ2TrendData: CCQ2TrendData = {
  monthlyTrends: [
    {
      month: "April",
      totalSales: codySalesData.kpis.totalSales,
      grossProfit: codySalesData.kpis.grossProfit,
      netProfit: codySalesData.kpis.netProfit,
      adSpend: codySalesData.kpis.adSpend,
      roi: codySalesData.kpis.adSpend > 0 ? codySalesData.kpis.grossProfit / codySalesData.kpis.adSpend : 0,
      grossMarginPct: codySalesData.kpis.grossProfitPercent,
      netMarginPct: codySalesData.kpis.netProfitPercent,
      salesCount: codySalesData.jobData.length,
      avgSaleValue: codySalesData.kpis.totalSales / codySalesData.jobData.length,
      expectedCommission: codySalesData.kpis.salesCommissions
    },
    {
      month: "May",
      totalSales: codyMayData.kpis.totalSales,
      grossProfit: codyMayData.kpis.grossProfit,
      netProfit: codyMayData.kpis.netProfit,
      adSpend: codyMayData.kpis.adSpend,
      roi: codyMayData.kpis.adSpend > 0 ? codyMayData.kpis.grossProfit / codyMayData.kpis.adSpend : 0,
      grossMarginPct: codyMayData.kpis.grossProfitPercent,
      netMarginPct: codyMayData.kpis.netProfitPercent,
      salesCount: codyMayData.jobData.length,
      avgSaleValue: codyMayData.kpis.totalSales / codyMayData.jobData.length,
      expectedCommission: codyMayData.kpis.salesCommissions
    },
    {
      month: "June",
      totalSales: codySalesJuneData.kpis.totalSales,
      grossProfit: codySalesJuneData.kpis.grossProfit,
      netProfit: codySalesJuneData.kpis.netProfit,
      adSpend: codySalesJuneData.kpis.adSpend,
      roi: codySalesJuneData.kpis.adSpend > 0 ? codySalesJuneData.kpis.grossProfit / codySalesJuneData.kpis.adSpend : 0,
      grossMarginPct: codySalesJuneData.kpis.grossProfitPercent,
      netMarginPct: codySalesJuneData.kpis.netProfitPercent,
      salesCount: codySalesJuneData.jobData.length,
      avgSaleValue: codySalesJuneData.kpis.totalSales / codySalesJuneData.jobData.length,
      expectedCommission: codySalesJuneData.kpis.salesCommissions
    }
  ],
  
  // Calculate source performance (Cody reports typically have no ad spend)
  sourcePerformance: (() => {
    const salesBySource = aggregateSalesBySource();
    const adSpendBySource = aggregateAdSpendBySource();
    
    return salesBySource.map(source => {
      const adSpendItem = adSpendBySource.find(ad => 
        ad.source.toLowerCase().includes(source.source.toLowerCase()) || 
        source.source.toLowerCase().includes(ad.source.toLowerCase())
      );
      
      const adSpend = adSpendItem?.spend || 0;
      const roi = adSpend > 0 ? source.grossProfit / adSpend : 0;
      const costPerSale = source.count > 0 ? adSpend / source.count : 0;
      
      return {
        ...source,
        adSpend,
        roi,
        costPerSale
      };
    }).sort((a, b) => b.totalSales - a.totalSales); // Sort by sales for Cody reports
  })(),
  
  // Generate lead source trends
  leadSourceTrends: (() => {
    // Get unique sources across all months
    const allSources = new Set<string>();
    [codySalesData.salesBySource, codyMayData.salesBySource, codySalesJuneData.salesBySource].forEach(monthSources => {
      monthSources.forEach(source => allSources.add(source.source));
    });
    
    return Array.from(allSources).map(sourceName => {
      // Find data for each month
      const aprilSource = codySalesData.salesBySource.find(s => s.source === sourceName) || 
                         { totalSales: 0, count: 0, grossProfit: 0 };
      const maySource = codyMayData.salesBySource.find(s => s.source === sourceName) || 
                       { totalSales: 0, count: 0, grossProfit: 0 };
      const juneSource = codySalesJuneData.salesBySource.find(s => s.source === sourceName) || 
                        { totalSales: 0, count: 0, grossProfit: 0 };
      
      // Cody reports typically have no ad spend, so ROI is 0
      
      return {
        source: sourceName,
        april: {
          sales: aprilSource.totalSales,
          count: aprilSource.count,
          roi: 0
        },
        may: {
          sales: maySource.totalSales,
          count: maySource.count,
          roi: 0
        },
        june: {
          sales: juneSource.totalSales,
          count: juneSource.count,
          roi: 0
        },
        q2Total: {
          sales: aprilSource.totalSales + maySource.totalSales + juneSource.totalSales,
          count: aprilSource.count + maySource.count + juneSource.count,
          roi: 0
        }
      };
    }).sort((a, b) => b.q2Total.sales - a.q2Total.sales);
  })()
};